import React, { useState } from 'react';
import { Alert, Container } from 'react-bootstrap';
import {
  PeopleFill,
  FileText,
  CreditCard,
  ExclamationTriangle,
  Gear,
  Wrench
} from 'react-bootstrap-icons';
import './Admin.css';
import AdminComplaints from './AdminComplaints';
import AdminPricingPlans from './AdminPricingPlans';
import UserManagement from './UserManagement';
import AdminReportsManagement from './AdminReportsManagement';
import DashboardOverview from './DashboardOverview';
import SectionHeader from './SectionHeader';
import DataSetup from './DataSetup';
import ApiKeyManagement from './ApiKeyManagement';
import AppSettings from './AppSettings';
import { useUserCount } from '../../hooks/useUserCount';
import { useDataSetup } from '../../hooks/useDataSetup';

const AdminPage = () => {
  const [error, setError] = useState(null);
  
  // --- Dashboard State ---
  const [activeSection, setActiveSection] = useState('dashboard'); // dashboard, users, reports, etc.
  
  // Custom hooks
  const { userCount, refreshUserCount } = useUserCount();
  const { setupLoading, setupStatus, seedPricingPlans, resetSetupStatus } = useDataSetup();
  
  // Handle section changes
  const handleSectionChange = (section) => {
    setActiveSection(section);
  };

  // Handle data setup errors
  const handleDataSetupError = (result) => {
    if (!result.success) {
      setError(result.error || "Failed to perform data setup operation.");
    } else {
      setError(null);
    }
  };

  // Enhanced seed pricing plans with error handling
  const handleSeedPricingPlans = async () => {
    const result = await seedPricingPlans();
    handleDataSetupError(result);
  };

  // Render Dashboard Overview
  const renderDashboard = () => (
    <div className="admin-section">
      <SectionHeader
        title="Admin Dashboard"
        icon={<Gear />}
        onBackClick={() => window.history.back()}
        description="Overview of your application's key metrics and quick access to all admin functions"
      />
      <DashboardOverview 
        userCount={userCount} 
        onSectionChange={handleSectionChange} 
      />
    </div>
  );

  // Render Users Section
  const renderUsers = () => (
    <div className="admin-section">
      <SectionHeader
        title="User Management"
        icon={<PeopleFill />}
        onBackClick={() => handleSectionChange('dashboard')}
        description="Manage user accounts, permissions, and subscription plans"
      />
      <UserManagement onUserModified={refreshUserCount} />
    </div>
  );

  // Render Reports Section
  const renderReports = () => (
    <div className="admin-section">
      <SectionHeader
        title="Report Management"
        icon={<FileText />}
        onBackClick={() => handleSectionChange('dashboard')}
        description="Create, edit, and manage investment reports with detailed recommendations"
      />
      <AdminReportsManagement />
    </div>
  );

  // Render Content based on active section
  const renderContent = () => {
    switch (activeSection) {
      case 'users':
        return renderUsers();
      case 'reports':
        return renderReports();
      case 'pricing':
        return (
          <div className="admin-section">
            <SectionHeader
              title="Pricing Plans"
              icon={<CreditCard />}
              onBackClick={() => handleSectionChange('dashboard')}
              description="Configure subscription tiers and pricing for different user plans"
            />
            <AdminPricingPlans />
          </div>
        );
      case 'setup':
        return (
          <div className="admin-section">
            <SectionHeader
              title="One-Time Data Setup"
              icon={<Wrench />}
              onBackClick={() => handleSectionChange('dashboard')}
              description="Initialize system data, seed pricing plans, and configure defaults"
            />
            <DataSetup
              setupLoading={setupLoading}
              setupStatus={setupStatus}
              onSeedPricingPlans={handleSeedPricingPlans}
              onResetSetupStatus={resetSetupStatus}
            />
          </div>
        );
      case 'complaints':
        return (
          <div className="admin-section">
            <SectionHeader
              title="Complaints Data"
              icon={<ExclamationTriangle />}
              onBackClick={() => handleSectionChange('dashboard')}
              description="Review and manage user complaints and feedback submissions"
            />
            <AdminComplaints />
          </div>
        );
      case 'external-api':
        return (
          <div className="admin-section">
            <SectionHeader
              title="External API Management"
              icon={<Gear />}
              onBackClick={() => handleSectionChange('dashboard')}
              description="Manage API keys for third-party access to user status information"
            />
            <ApiKeyManagement />
          </div>
        );
      case 'settings':
        return (
          <div className="admin-section">
            <SectionHeader
              title="App Settings"
              icon={<Gear />}
              onBackClick={() => handleSectionChange('dashboard')}
              description="Configure application-level feature flags and toggles"
            />
            <AppSettings />
          </div>
        );
      default:
        return renderDashboard();
    }
  };

  // Main render
  return (
    <div className="admin-page">
      <Container fluid>
        {/* Error Display */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)} className="mt-3">
            {error}
          </Alert>
        )}

        {/* Main Content */}
        {renderContent()}
      </Container>
    </div>
  );
};

export default AdminPage; 